import React from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Tooltip, Typography } from "antd";
import { selectIsPhiAllowed } from "../../store/selector";
import { selectPatientDetails } from "../../store/selector";
import { QUICK_ACTION_KEYS } from "../../constant/consent";
import useStandardizedTracking, {
  EventNames,
  ContentTypes,
  FeatureGroups
} from "../../utils/pendo/useStandardizedTracking";
import { ReactComponent as SendIcon } from "../../assets/svg/send-icon.svg";
import { ReactComponent as RemoveIcon } from "../../assets/svg/remove-icon.svg";
import { ReactComponent as AddCircleIcon } from "../../assets/svg/Add-Circle.svg";
import { ReactComponent as CircleCloseIcon } from "../../assets/svg/circle-close.svg";
import {
  WHITE,
  BLACK,
  PRIMARY_600,
  PRIMARY_100,
  PRIMARY_200,
  ERROR_600,
  ERROR_200,
  GRAY_300,
  GRAY_400
} from "../../constant/colors";
import { fontWeights, sizes } from "../../constant/styles";
import { CONSENT_EVENT_TYPES } from "../../constant/consent";
import { roles } from "../../constant/roles";
import { selectUser } from "../../store/selector";

const { Text } = Typography;

const createWhiteIcon = (IconComponent, colorProperty = "stroke") => styled(IconComponent)`
  width: 12px;
  height: 12px;
  path {
    ${colorProperty}: ${WHITE};
  }
`;

const WhiteSendIcon = createWhiteIcon(SendIcon, "stroke");
const WhitePlusIcon = createWhiteIcon(AddCircleIcon, "stroke");
const WhiteRemoveIcon = createWhiteIcon(RemoveIcon, "stroke");
const WhiteCircleCloseIcon = createWhiteIcon(CircleCloseIcon, "stroke");

const QuickActionsContainer = styled.div`
  display: flex;
  gap: 10px;
  justify-content: stretch;
  align-items: stretch;
  padding: 20px 0px;
`;

const ActionButton = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: ${(props) => (props.hasContent ? "5px 10px" : "10px")};
  border-radius: 10px;
  border: 1px solid ${(props) => (props.disabled ? GRAY_300 : props.borderColor)};
  flex: 1;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  background-color: ${(props) => {
    if (props.disabled) return WHITE;
    return props.isSelected ? props.selectedBackgroundColor : WHITE;
  }};
  opacity: ${(props) => (props.disabled ? 0.6 : 1)};
`;

const ActionIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5px;
  border-radius: 100px;
  width: 22px;
  height: 22px;
  background-color: ${(props) => (props.disabled ? GRAY_400 : props.iconBackgroundColor)};
`;

const ActionText = styled.div`
  font-weight: ${fontWeights.semibold};
  font-size: ${(props) => (props.hasContent ? sizes.small : sizes.medium)};
  line-height: 1.5em;
  flex: 1;
  color: ${BLACK};
`;

/**
 * ConsentQuickActions component
 * Renders the four quick action buttons: Request, Register, Revoke, Decline
 * @param {Function} onAction - Callback for action clicks
 * @param {string} selectedAction - Currently selected action key
 * @param {boolean} hasContent - Whether content section is visible
 */
const ConsentQuickActions = ({ onAction, selectedAction, hasContent }) => {
  const { t } = useTranslation();
  const user = useSelector(selectUser);
  const canPerformConsentActions = useSelector(selectIsPhiAllowed) && !(user.role === roles.OPERATION_MANGER);
  const patientDetails = useSelector(selectPatientDetails);
  const hasActiveConsent = patientDetails?.latestConsentEvent?.type === CONSENT_EVENT_TYPES.CONSENT_ACCEPTED;
  const { track } = useStandardizedTracking();

  const quickActions = [
    {
      key: QUICK_ACTION_KEYS.REQUEST,
      label: t("consent.actions.request_consent"),
      icon: <WhiteSendIcon />,
      iconBackgroundColor: PRIMARY_600,
      selectedBackgroundColor: PRIMARY_100,
      borderColor: PRIMARY_200,
      disabled: !canPerformConsentActions,
      tooltipText: !canPerformConsentActions ? t("consent.tooltips.action_not_allowed") : null
    },
    {
      key: QUICK_ACTION_KEYS.REGISTER,
      label: t("consent.actions.register_consent"),
      icon: <WhitePlusIcon />,
      iconBackgroundColor: PRIMARY_600,
      selectedBackgroundColor: PRIMARY_100,
      borderColor: PRIMARY_200,
      disabled: !canPerformConsentActions,
      tooltipText: !canPerformConsentActions ? t("consent.tooltips.action_not_allowed") : null
    },
    {
      key: QUICK_ACTION_KEYS.REVOKE,
      label: t("consent.actions.revoke_consent"),
      icon: <WhiteRemoveIcon />,
      iconBackgroundColor: ERROR_600,
      selectedBackgroundColor: ERROR_200,
      borderColor: ERROR_200,
      disabled: !hasActiveConsent || !canPerformConsentActions,
      tooltipText: !canPerformConsentActions
        ? t("consent.tooltips.action_not_allowed")
        : !hasActiveConsent
        ? t("consent.tooltips.revoke_disabled")
        : null
    },
    {
      // MVP-29825. Blocked while consent is ACTIVE — the mirror image of
      // Revoke's rule. Recording a refusal on top of a live signed consent
      // would leave two contradictory facts on the patient; the navigator
      // should revoke first, which the tooltip says.
      key: QUICK_ACTION_KEYS.DECLINE,
      label: t("consent.actions.decline_consent"),
      icon: <WhiteCircleCloseIcon />,
      iconBackgroundColor: ERROR_600,
      selectedBackgroundColor: ERROR_200,
      borderColor: ERROR_200,
      disabled: hasActiveConsent || !canPerformConsentActions,
      tooltipText: !canPerformConsentActions
        ? t("consent.tooltips.action_not_allowed")
        : hasActiveConsent
        ? t("consent.tooltips.decline_disabled")
        : null
    }
  ];

  const handleActionClick = (actionKey, isDisabled) => {
    if (isDisabled) {
      return;
    }

    // Step 3/3a: Track Pendo event for quick action button clicks
    const buttonLabels = {
      [QUICK_ACTION_KEYS.REQUEST]: t("consent.actions.request_consent"),
      [QUICK_ACTION_KEYS.REGISTER]: t("consent.actions.register_consent"),
      [QUICK_ACTION_KEYS.REVOKE]: t("consent.actions.revoke_consent"),
      [QUICK_ACTION_KEYS.DECLINE]: t("consent.actions.decline_consent")
    };

    const contentKeys = {
      [QUICK_ACTION_KEYS.REQUEST]: "request_consent_button",
      [QUICK_ACTION_KEYS.REGISTER]: "record_consent_button",
      [QUICK_ACTION_KEYS.REVOKE]: "revoke_consent_button",
      [QUICK_ACTION_KEYS.DECLINE]: "decline_consent_button"
    };

    const buttonIds = {
      [QUICK_ACTION_KEYS.REQUEST]: "btn_request_consent",
      [QUICK_ACTION_KEYS.REGISTER]: "btn_record_consent",
      [QUICK_ACTION_KEYS.REVOKE]: "btn_revoke_consent",
      [QUICK_ACTION_KEYS.DECLINE]: "btn_decline_consent"
    };

    track(EventNames.BUTTON_CLICK, {
      pageKey: "patient_overview",
      featureGroup: FeatureGroups.ECONSENT,
      contentType: ContentTypes.CONSENT,
      contentKey: contentKeys[actionKey],
      additionalInfo: {
        button_text: buttonLabels[actionKey],
        button_id: buttonIds[actionKey],
        popup_context: "consent_management_popup"
      }
    });

    if (onAction) {
      onAction(actionKey);
    }
    console.log(`${actionKey} consent action clicked`);
  };

  return (
    <QuickActionsContainer hasContent={hasContent}>
      {quickActions.map((action) => {
        const isSelected = selectedAction === action.key;
        const isDisabled = !!action.disabled;
        const hasTooltip = !!action.tooltipText;

        const actionButton = (
          <ActionButton
            key={action.key}
            isSelected={isSelected}
            selectedBackgroundColor={action.selectedBackgroundColor}
            borderColor={action.borderColor}
            hasContent={hasContent}
            disabled={isDisabled}
            onClick={() => handleActionClick(action.key, isDisabled)}
          >
            <ActionIcon iconBackgroundColor={action.iconBackgroundColor} disabled={isDisabled}>
              {action.icon}
            </ActionIcon>
            <ActionText hasContent={hasContent}>{action.label}</ActionText>
          </ActionButton>
        );

        if (hasTooltip) {
          return (
            <Tooltip
              key={action.key}
              title={<Text>{action.tooltipText}</Text>}
              placement="bottom"
              color={WHITE}
              overlayInnerStyle={{
                padding: "8px 12px",
                fontSize: sizes.small,
                fontWeight: fontWeights.semibold,
                textAlign: "center"
              }}
            >
              {actionButton}
            </Tooltip>
          );
        }

        return actionButton;
      })}
    </QuickActionsContainer>
  );
};

export default ConsentQuickActions;
